// eslint-disable-next-line no-shadow
import get from 'lodash/get';
import filter from 'lodash/filter';
import find from 'lodash/find';
import flatten from 'lodash/flatten';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import replace from 'lodash/replace';
import size from 'lodash/size';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import isString from 'lodash/isString';
import startsWith from 'lodash/startsWith';

import { setWindowLocation } from 'js-utils/http';
import { logCheckpointOnPageChange, logCheckpointViaAjax } from '@eb/janus';
import gettext from '@eb/gettext';
import { deepKeysToCamel } from '@eb/transformation-utils';

import { events as eventsDuck } from '@eb/destination-utils';

import {
    GA_BOOKMARK_ATTEMPT_ACTION,
    GA_BOOKMARK_EVENT_ACTION,
    GA_HOME_CATEGORY,
    GA_HOME_FEED_CATEGORY,
    GA_SEE_MORE_ACTION,
    GA_UNBOOKMARK_EVENT_ACTION,
    GA_VIEW_EVENT_ACTION,
    GA_SWITCH_TAB,
    GA_SHARE_ATTEMPT,
    GA_SLIDES_LIMIT_REACHED_ACTION,
    GA_ONBOARDING_START_ACTION,
    GA_FOLLOW_ORGANIZER_ACTION,
} from '../../constants/analytics';
import {
    LIKED_EVENTS,
    BOOKMARK_EVENT_FEED,
    SEE_MORE_FEED,
    VIEW_EVENT_FEED,
    SMALL_CITY_FALLBACK,
} from '../../constants/constants';
import {
    TABS_CONFIG,
    ALL_TAB,
    SEASONAL_TAB,
    FOR_YOU,
} from '../../constants/tabConfig';
import {
    getFollowedProfiles,
    getUserSavedEvents,
    searchEvents,
    searchEventsWithParameters,
    fetchCityBrowseEvents as fetchCityBrowseEventsApi,
} from './api';
import {
    getTransformedContent,
    transformBucketsContent,
    transformSearchContent,
} from '../../utils/transformation';
import { buildLocationData } from '../../utils/location';
import { retrieveHomeFeedState } from '../../utils/sessionStorage';
import {
    updateContent,
    updateTabKey,
    verifyPoorContent,
    fetchCollectionsForPlace,
} from '../content/actions';
import { getOrganizersUserFollow } from '../organizers/actions';
import {
    setSearchCallAsSuccess,
    setSearchCallAsFailed,
    setSearchCallAsOngoing,
} from './searchActions';
import {
    setCityBrowseCallAsSuccess,
    setCityBrowseCallAsFailed,
    setCityBrowseCallAsOngoing,
} from './cityBrowseActions';

const {
    api: {
        saveEvent: saveEventApi,
        unsaveEvent: unsaveEventApi,
        loggedOutSaveRedirect,
    },
} = eventsDuck;

const trackViewEvent = ({ bucketKey, eventId, tabKey, loggedIn = true }) => ({
    type: 'VIEW_EVENT',
    meta: {
        analytics: {
            category: loggedIn ? GA_HOME_FEED_CATEGORY : GA_HOME_CATEGORY,
            action: GA_VIEW_EVENT_ACTION,
            label: replace(`${bucketKey}/${tabKey}`, ' ', ''),
            dimensions: {
                eventId,
            },
        },
    },
});

const trackSaveEvent = ({ eventId, savedByYou }) => ({
    type: 'SAVE_EVENT',
    meta: {
        analytics: {
            category: GA_HOME_FEED_CATEGORY,
            action: savedByYou
                ? GA_BOOKMARK_EVENT_ACTION
                : GA_UNBOOKMARK_EVENT_ACTION,
            label: eventId,
            dimensions: {
                eventId,
            },
        },
    },
});

export const trackTabChange = ({ tabKey, loggedIn = true }) => ({
    type: 'CHANGE_TAB',
    meta: {
        analytics: {
            category: loggedIn ? GA_HOME_FEED_CATEGORY : GA_HOME_CATEGORY,
            action: GA_SWITCH_TAB,
            label: tabKey,
        },
    },
});

export const clickOnViewEvent = (eventId, bucketKey) => (
    dispatch,
    getState,
) => {
    const {
        app: { domain },
        content: { browseState },
        user: { isAuthenticated },
    } = getState();

    logCheckpointOnPageChange(VIEW_EVENT_FEED, domain);

    const tabKeyGA = startsWith(browseState.tabKey, SEASONAL_TAB)
        ? browseState.tabKeyLabel
        : browseState.tabKey;

    dispatch(
        trackViewEvent({
            bucketKey,
            eventId,
            tabKey: tabKeyGA,
            loggedIn: isAuthenticated,
        }),
    );
};

const trackSeeMore = ({ bucketKey, tabKey, loggedIn = true }) => ({
    type: 'SEE_MORE_ACTION',
    meta: {
        analytics: {
            category: loggedIn ? GA_HOME_FEED_CATEGORY : GA_HOME_CATEGORY,
            action: GA_SEE_MORE_ACTION,
            label: `${bucketKey}/${tabKey}`,
        },
    },
});

export const clickOnSeeMore = (url, bucketKey) => (dispatch, getState) => {
    const state = getState();
    const {
        user: { isAuthenticated },
        content: {
            browseState: { tabKeyLabel },
        },
    } = state;

    logCheckpointViaAjax(SEE_MORE_FEED);

    dispatch(
        trackSeeMore({
            bucketKey,
            tabKey: tabKeyLabel,
            loggedIn: isAuthenticated,
        }),
    );

    setWindowLocation(url);
};

export const UPDATE_EVENT_SAVED_BY_YOU = 'UPDATE_EVENT_SAVED_BY_YOU';
const updateEventSavedByYou = (eventId, savedByYou, tabKey = null) => ({
    type: UPDATE_EVENT_SAVED_BY_YOU,
    payload: {
        eventId,
        savedByYou,
        tabKey,
    },
});

export const UPDATE_LIKES_EVENT_BUCKET = 'UPDATE_LIKES_EVENT_BUCKET';
const updateLikesEventBucket = (events, eventIds) => ({
    type: UPDATE_LIKES_EVENT_BUCKET,
    payload: {
        events,
        eventIds,
    },
});

const _fetchUserSavedEvents = () => (dispatch) =>
    getUserSavedEvents().then((response) => {
        const {
            entities: { events: likedEvents } = {},
            result: { events: likedEventIds },
        } = getTransformedContent('events', response);

        dispatch(updateLikesEventBucket(likedEvents, likedEventIds));
    });

const _loggedInSaveAction = (dispatch, state, eventId, shouldSave) => {
    const {
        content: { entityContext, browseState },
    } = state;
    const userSavedEvents = find(entityContext, { key: LIKED_EVENTS });
    const { tabKey } = browseState;

    if (shouldSave) {
        // optimistic update
        dispatch(updateEventSavedByYou(eventId, true));

        logCheckpointViaAjax(BOOKMARK_EVENT_FEED);

        return saveEventApi(eventId)
            .then(() => {
                dispatch(
                    trackSaveEvent({
                        eventId,
                        savedByYou: true,
                    }),
                );
                // Resolved promise so view knows if the event save was successful
                return Promise.resolve('success');
            })
            .catch((e) => {
                if (e.parsedError.error === 'BOOKMARK_ALREADY_EXISTS') {
                    dispatch(updateEventSavedByYou(eventId, true));
                } else {
                    dispatch(updateEventSavedByYou(eventId, false));
                }
            });
    }

    // optimistic update
    dispatch(updateEventSavedByYou(eventId, false, tabKey));

    return unsaveEventApi(eventId)
        .then(() => {
            dispatch(
                trackSaveEvent({
                    eventId,
                    savedByYou: false,
                }),
            );

            // fetch more events if we don't have enough to display
            if (
                has(userSavedEvents, 'events') &&
                size(userSavedEvents.events) < 8
            ) {
                setTimeout(() => {
                    dispatch(_fetchUserSavedEvents());
                }, 1000);
            }

            // Resolved promise so view knows that the event unsave was successful
            return Promise.resolve('success');
        })
        .catch(() => {
            dispatch(updateEventSavedByYou(eventId, true));
        });
};

const _trackBookmarkAttempt = ({ eventId }) => ({
    type: 'BOOKMARK_ATTEMPT',
    meta: {
        analytics: {
            category: GA_HOME_CATEGORY,
            action: GA_BOOKMARK_ATTEMPT_ACTION,
            label: eventId,
            dimensions: {
                eventId,
            },
        },
    },
});

export const clickOnSaveEvent = (eventId, shouldSave) => (
    dispatch,
    getState,
) => {
    const state = getState();
    const {
        user: { isAuthenticated },
    } = state;

    if (isAuthenticated) {
        return _loggedInSaveAction(dispatch, state, eventId, shouldSave);
    }

    dispatch(_trackBookmarkAttempt({ eventId }));

    return loggedOutSaveRedirect(eventId, { pathname: '/' });
};

const finishFetchCityBrowseEvents = (
    dispatch,
    entities,
    entityContext,
    fetchSuccess = true,
) => {
    dispatch(
        updateContent({
            entities,
            entityContext,
        }),
    );

    if (fetchSuccess) {
        dispatch(setCityBrowseCallAsSuccess());
    } else {
        dispatch(setCityBrowseCallAsFailed());
    }

    // We should enable this later when we use CityBrowse for more things.
    // dispatch(verifyPoorContent({buckets: entityContext, ActionName: 'search_location_change'}));
};

export const fetchCityBrowseEvents = ({ locationData } = {}) => (
    dispatch,
    getState,
) => {
    const { location } = getState();

    dispatch(setCityBrowseCallAsOngoing());

    const _locationData = isEmpty(locationData) ? location : locationData;
    const { placeId, isOnline } = _locationData;

    if (placeId && !isOnline) {
        return fetchCityBrowseEventsApi(placeId).then(({ buckets }) => {
            let { entities, entityContext } = transformBucketsContent(buckets);

            const smallCityFallbackBucket = find(entityContext, {
                key: SMALL_CITY_FALLBACK,
            });

            // When small_city_fallback, fetch more using the search endpoint events and append them to the same bucket
            if (
                smallCityFallbackBucket &&
                size(smallCityFallbackBucket.events) < 32
            ) {
                return searchEvents(placeId).then((response) => {
                    const {
                        entities: newEntities,
                        ids,
                    } = transformSearchContent(response);

                    entities = {
                        ...entities,
                        ...newEntities,
                    };

                    entityContext = filter(
                        entityContext,
                        ({ key }) => key !== SMALL_CITY_FALLBACK,
                    );
                    entityContext = [
                        ...entityContext,
                        {
                            ...smallCityFallbackBucket,
                            events: uniq([
                                ...ids,
                                ...smallCityFallbackBucket.events,
                            ]),
                        },
                    ];

                    finishFetchCityBrowseEvents(
                        dispatch,
                        entities,
                        entityContext,
                    );
                });
            }

            return finishFetchCityBrowseEvents(
                dispatch,
                entities,
                entityContext,
            );
        });
    }

    const { entities, entityContext } = transformBucketsContent([]);

    return finishFetchCityBrowseEvents(
        dispatch,
        entities,
        entityContext,
        false,
    );
};

export const UPDATE_TAB_CONTENT = 'UPDATE_TAB_CONTENT';

const buildEventSearch = ({ flatBucket, tabKey }) => {
    const { eventSearch } = TABS_CONFIG[tabKey];

    // eventSearch is empty when is called from switch location
    const _eventSearch = isEmpty(eventSearch)
        ? get(flatBucket, 'eventSearch', { dates: ['current_future'] })
        : eventSearch;

    if (
        tabKey === SEASONAL_TAB &&
        !isEmpty(_eventSearch) &&
        !isString(_eventSearch.q)
    ) {
        _eventSearch.q = _eventSearch.q ? _eventSearch.q.toString() : '';
    }

    return _eventSearch;
};

const getAndUpdateTabKeyLabel = ({ tabKey }) => (dispatch, getState) => {
    const {
        user: { isAuthenticated },
        content: { seasonal } = {},
    } = getState();

    let { tabKeyLabel } = TABS_CONFIG[tabKey];
    let contentLabel = tabKeyLabel;

    // get actual TabKey to check if is a real tab change or it is tab redirected from logged in/out
    const actualTabKey = retrieveHomeFeedState({ seasonalContent: seasonal })
        .tabKey;

    if (actualTabKey !== tabKey) {
        if (tabKey === SEASONAL_TAB) {
            const seasonalTabLabel = get(
                seasonal,
                'byDate.key',
                get(seasonal, 'byDate.tabName', ''),
            );
            tabKeyLabel = seasonalTabLabel ? seasonalTabLabel : tabKeyLabel;
        }

        dispatch(
            trackTabChange({
                loggedIn: isAuthenticated,
                tabKey: replace(tabKeyLabel, ' ', ''),
            }),
        );
        dispatch(updateTabKey({ tabKey, tabKeyLabel }));
        contentLabel = `on_tab_change-${tabKeyLabel}`;
    }

    return contentLabel;
};

const fetchEventsForRegularTab = ({
    locationData,
    tabKey = ALL_TAB,
    shouldUpdateContent,
}) => (dispatch, getState) => {
    dispatch(setSearchCallAsOngoing());

    const {
        app: { featureFlags },
        location,
        content: { flatBucket } = {},
    } = getState();

    const { follow, hasFiveToFollow } = TABS_CONFIG[tabKey];

    const _locationData = buildLocationData({ location, locationData });
    const { placeId, bbox, isOnline } = _locationData;

    const _eventSearch = buildEventSearch({
        flatBucket,
        tabKey,
    });
    const poorContentLabel = dispatch(getAndUpdateTabKeyLabel({ tabKey }));

    // followTags is empty when the bucket should not be rendered
    const followTags = follow;
    const _followTags = isEmpty(followTags)
        ? get(flatBucket, 'followTags', [])
        : followTags;

    /**
     * In some cases, the placeId is not present and also there are no events in the bucket.
     * This happens in smaller cities or BBox places.
     * The general idea is to try another request directly in the frontend to ensure we DO NOT
     * have any events to show.
     * Ideally the backend will solve this soon and we should remove this condition when that happens:
     * https://jira.evbhome.com/browse/EB-129013
     */
    const shouldReRequest =
        !placeId && flatBucket && isEmpty(flatBucket.results);

    if (shouldUpdateContent || shouldReRequest) {
        if (!featureFlags.disableCityBrowseOnFeed) {
            if (tabKey === ALL_TAB && featureFlags.launchEditorsPickInFeed) {
                dispatch(
                    fetchCityBrowseEvents({ locationData: _locationData }),
                );
            }
        }
        if (tabKey === ALL_TAB) {
            dispatch(fetchCollectionsForPlace(tabKey));
        }

        return searchEventsWithParameters({
            placeId,
            bbox,
            eventSearch: _eventSearch,
            isOnline,
        }).then(({ events }) => {
            dispatch({
                type: UPDATE_TAB_CONTENT,
                payload: {
                    events,
                    eventSearch: _eventSearch,
                    follow: _followTags,
                    hasFiveToFollow,
                },
            });
            dispatch(
                verifyPoorContent({ events, actionName: poorContentLabel }),
            );

            // if (!isEmpty(_followTags)) {
            //     dispatch(fetchOrganizersForLocation(_locationData, _followTags));
            // }
            dispatch(getOrganizersUserFollow());
            dispatch(setSearchCallAsSuccess());
        });
    }

    dispatch(
        verifyPoorContent({
            events: _eventSearch,
            actionName: poorContentLabel,
        }),
    );

    dispatch(getOrganizersUserFollow());

    return dispatch(setSearchCallAsFailed());
};

// Fetch events from Search endpoint
const fetchEventsForYouTab = ({ tabKey }) => async (dispatch, getState) => {
    dispatch(setSearchCallAsOngoing());

    const {
        user: { isAuthenticated },
        content: { flatBucket } = {},
    } = getState();

    const { tabKeyLabel, eventSearch, follow, hasFiveToFollow } = TABS_CONFIG[
        tabKey
    ];

    const followTags = follow;

    // eventSearch is empty when is called from switch location or saved events
    const _eventSearch = isEmpty(eventSearch)
        ? get(flatBucket, 'eventSearch', { dates: ['current_future'] })
        : eventSearch;

    // followTags is empty when the bucket should not be rendered
    const _followTags = isEmpty(followTags)
        ? get(flatBucket, 'followTags', [])
        : followTags;

    dispatch(getAndUpdateTabKeyLabel({ tabKey }));

    if (!isAuthenticated) {
        dispatch(setSearchCallAsSuccess());
        return dispatch({
            type: UPDATE_TAB_CONTENT,
            payload: {
                events: [],
                eventSearch: _eventSearch,
                follow: _followTags,
                hasFiveToFollow,
            },
        });
    }

    const followedProfileEvents = await getFollowedProfiles().then(
        (response) => {
            const events = response.events.results;

            if (!isEmpty(events)) {
                const entityContext = [
                    {
                        key: 'followed_profiles',
                        type: 'event',
                        name: gettext('From organizers you follow'),
                        events: events.map((event) => event.id),
                    },
                ];

                const entities = {};

                events.forEach((event) => {
                    entities[event.id] = deepKeysToCamel(event);
                });

                dispatch(updateContent({ entityContext, entities }));

                dispatch(
                    verifyPoorContent({
                        events,
                        actionName: `on_tab_change-${tabKeyLabel}`,
                    }),
                );
            }
        },
    );

    const userSavedEvents = await getUserSavedEvents().then((results) => {
        const sortedEvents = sortBy(
            results.events,
            (event) => event.start_date,
        );
        const events = {
            pagination: results.pagination,
            results: sortedEvents,
        };

        dispatch({
            type: UPDATE_TAB_CONTENT,
            payload: {
                events,
                eventSearch: _eventSearch,
                follow: _followTags,
                hasFiveToFollow,
            },
        });
        dispatch(
            verifyPoorContent({
                events,
                actionName: `on_tab_change-${tabKeyLabel}`,
            }),
        );
    });

    // We always get a success here? it is different from the regular tab that has a success and a failure.
    dispatch(setSearchCallAsSuccess());

    return flatten(followedProfileEvents, userSavedEvents);
};

// Fetch events from Search endpoint
export const fetchEventsForTab = ({
    locationData,
    tabKey = ALL_TAB,
    shouldUpdateContent,
}) => {
    if (tabKey === FOR_YOU) {
        return fetchEventsForYouTab({ tabKey });
    }

    return fetchEventsForRegularTab({
        locationData,
        tabKey,
        shouldUpdateContent,
    });
};

const trackShareAttempt = ({ eventId, loggedIn = true }) => ({
    type: 'SHARE_ATTEMPT',
    meta: {
        analytics: {
            category: loggedIn ? GA_HOME_FEED_CATEGORY : GA_HOME_CATEGORY,
            action: GA_SHARE_ATTEMPT,
            label: eventId,
            dimensions: {
                eventId,
            },
        },
    },
});

export const clickOnShareEvent = (eventId) => (dispatch, getState) => {
    const {
        user: { isAuthenticated },
    } = getState();

    dispatch(
        trackShareAttempt({
            eventId,
            loggedIn: isAuthenticated,
        }),
    );
};

const trackOnboardingClick = ({ loggedIn = true }) => ({
    type: 'ONBOARDING_CARD_CLICK',
    meta: {
        analytics: {
            category: loggedIn ? GA_HOME_FEED_CATEGORY : GA_HOME_CATEGORY,
            action: GA_ONBOARDING_START_ACTION,
            label: '',
            onWindowUnload: true,
        },
    },
});

export const clickOnOnboardingCard = () => (dispatch, getState) => {
    const {
        user: { isAuthenticated },
    } = getState();

    dispatch(
        trackOnboardingClick({
            loggedIn: isAuthenticated,
        }),
    );
};

const trackOnFiveToFollow = ({ eventId }) => ({
    type: 'FTF_FOLLOW_ORGANIZER',
    meta: {
        analytics: {
            category: GA_HOME_FEED_CATEGORY,
            action: GA_FOLLOW_ORGANIZER_ACTION,
            label: `FiveToFollow|${eventId}`,
            onWindowUnload: true,
            dimensions: {
                locationOnPage: 'five_to_follow',
            },
        },
    },
});

export const clickOn5TFFollowOrganizer = (eventId) => (dispatch, getState) => {
    const {
        user: { isAuthenticated },
    } = getState();

    dispatch(
        trackOnFiveToFollow({
            eventId,
            loggedIn: isAuthenticated,
        }),
    );
};

const trackFiveToFollowNavigate = ({
    index,
    action,
    organizerId,
    loggedIn,
}) => ({
    type: 'FTF_NAVIGATE',
    meta: {
        analytics: {
            category: loggedIn ? GA_HOME_FEED_CATEGORY : GA_HOME_CATEGORY,
            action,
            label: `${index}|${organizerId}`,
            dimensions: {
                locationOnPage: 'five_to_follow',
            },
        },
    },
});

export const navigateOnFiveToFollow = ({ index, action, organizerId }) => (
    dispatch,
    getState,
) => {
    const {
        user: { isAuthenticated },
    } = getState();

    dispatch(
        trackFiveToFollowNavigate({
            index,
            action,
            organizerId,
            loggedIn: isAuthenticated,
        }),
    );
};

const trackFiveToFollowLimitReached = ({ loggedIn }) => ({
    type: 'FTF_LIMIT_REACHED',
    meta: {
        analytics: {
            category: loggedIn ? GA_HOME_FEED_CATEGORY : GA_HOME_CATEGORY,
            action: GA_SLIDES_LIMIT_REACHED_ACTION,
            dimensions: {
                locationOnPage: 'five_to_follow',
            },
        },
    },
});

export const limitReachedOnFiveToFollow = () => (dispatch, getState) => {
    const {
        user: { isAuthenticated },
    } = getState();

    dispatch(
        trackFiveToFollowLimitReached({
            loggedIn: isAuthenticated,
        }),
    );
};
